/**
 * introTour.js
 * Step definitions for the Introduction Tour.
 *
 * The "content" strings are intentionally kept loose / placeholder-friendly.
 * Rewrite the copy freely before or after implementation — the structure won't change.
 *
 * react-joyride step shape:
 *   { target, title, content, placement, disableBeacon, spotlightClicks }
 *
 * Steps that need the user to navigate to a different page (e.g. click "Create new")
 * use `disableBeacon: true` and `spotlightClicks: true` so the user can interact
 * with the highlighted element while the tooltip is open.
 */

const introSteps = [
  // ── Step 1: Welcome splash ───────────────────────────────────────────────
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: '🎉 Welcome to Story Weaver!',
    content:
      'Story Weaver helps you build story ideas, worlds, and lore using physical card decks — digitally. ' +
      'This short tour will walk you through your first story seed.\n\n' +
      'Ready? Let\'s go!\n\n\n' +
      'This website is a digital implementation of\n<a href="https://storyenginedeck.com/">The Story Engine website</a>.',
  },
  // ── Step 2: Interactive tour ───────────────────────────────────────────────
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: '⚡ This is an interactive tour!',
    content:
      'IMPORTANT: You will have to perform the suggested actions in the tour to get the full experience.',
  },

  // ── Step 3: Create a canvas ──────────────────────────────────────────────
  {
    target: '#tour-create-canvas',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: '✨ Create your first Canvas',
    content:
      'Now click "Create new" to open a fresh canvas. A canvas is your creative workspace — ' +
      'you can have as many as you like.',
  },
  // ── Step 4: Create a canvas ──────────────────────────────────────────────
  {
    target: '#tour-create-canvas',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: '✨ Important!',
    content:
      'Now CLICK "Create new" to open a fresh canvas.',
  },

  // ── Step 5: Meet the decks (body-centered, informational) ───────────────
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: '🗂️ Meet the Decks',
    content:
      'In StoryWeaver you draw cards and arrange them on a canvas to create stories and world lore. ' +
      'The cards are grouped into families that help you focus on a particular aspect of your story. ' +
      'Story Weaver has three deck families:\n\n' +
      '📖 The Story Engine — generate story ideas\n' +
      '🌍 Deck of Worlds — create places and worlds\n' +
      '📜 Lore Masters Deck — build lore and history\n\n' +
      'Each deck has its own in-depth tour available from the 🗺️ Guided Tours menu. ' +
      'For now, we\'ll do a quick intro using The Story Engine deck.',
  },
  // ── Step 6: Select decks (right panel) ──────────────────────────────────
  {
    target: '#tour-deck-selector',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '📚 Choose Your Decks',
    content:
      'Depending on what you like to create you can choose different decks. ' +
      'The available decks are displayed here, grouped by family.\n\n' +
      'Click on the arrow behind "The Story Engine" (the deck with the yellow sun logo) to expand the list of its available expansions.',
  },
  // ── Step 7: Select decks (right panel) ──────────────────────────────────
  {
    target: '#tour-deck-selector',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '📚 Choose Your Decks',
    content:
      'For this tour, tick the box for the main deck "The Story Engine". This makes the decks available to draw from in the LEFT panel.',
  },

  // ── Step 8: Left panel ───────────────────────────────────────────────────
  {
    target: '#tour-canvas-panel-left',
    placement: 'right',
    disableBeacon: true,
    title: '🃏 Your Card Library',
    content:
      'The left panel now shows the card types for your chosen deck. ' +
      'Each icon represents a card type you can draw onto the canvas.',
  },
  // ── Step 9: Draw an Agent card ───────────────────────────────────────────
  {
    target: '#tour-deck-1-card-1',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: '👤 Who is your character?',
    content:
      'Click the Agent button to draw your first card.\n' +
      'We\'re about to meet the main character of our story.',
  },
  // ── Step 10: Your new card appears ──────────────────────────────────────────
  {
    target: '#tour-canvas-grid',
    placement: 'top-start',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '👤 A card appeared on the canvas!',
    content:
      'Your first card has appeared near the center of the canvas.\n\n' +
      'The blue border indicates it\'s selected, and the right panel shows the current properties of the card. ',
  },
  // ── Step 11: Select an Agent option ──────────────────────────────────
  {
    target: '#tour-card-properties',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '✏️ Pick an Option',
    content:
      'A card appeared on the canvas! It is selected by default, so you can immediately look ' +
      'at the right panel and choose the option that speaks to you.\n' +
      'You can see the text on the card update as you choose different options.',
  },

  // ── Step 12: Draw an Engine card ─────────────────────────────────────────
  {
    target: '#tour-deck-1-card-5',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: '⚙️ What drives them?',
    content: 'Click the Engine button to draw a motivation card for your character.',
  },
  // ── Step 13: Arrange Engine card ──────────────────────────────────────────
  {
    target: '#tour-canvas-grid',
    placement: 'top-start',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '📐 Arrange the Card',
    content:
      'Now drag the card, using the left mouse button, to the right of the Actor card.\n\n' +
      'Don\'t worry about the exact position, you can always adjust it later.',
  },
  // ── Step 14: Select an Engine option ──────────────────────────────────
  {
    target: '#tour-card-properties',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '✏️ Pick an Option',
    content:
      'Not all cards have the same number of options. An Engine card only has 2 options, look at the right panel and choose the option that speaks to you.',
  },

  // ── Step 15: Draw an Anchor card ─────────────────────────────────────────
  {
    target: '#tour-deck-1-card-2',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: '⚓ Of what?',
    content: 'Click the Anchor button — this card fills in what the Engine card is about. ' +
      'Later you can also use different card types to find out what the Engine card is about. ' +
      'For now we will use the Anchor card.',
  },
  // ── Step 16: Arrange Anchor card ──────────────────────────────────────────
  {
    target: '#tour-canvas-grid',
    placement: 'top-start',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '📐 Arrange the Card',
    content:
      'Now drag the card to the right of the Engine card.',
  },
  // ── Step 17: Select an Anchor option ──────────────────────────────────
  {
    target: '#tour-card-properties',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '✏️ Pick an Option',
    content:
      'Look at the right panel and choose the option that fits your story.',
  },

  // ── Step 18: Draw a Conflict card ────────────────────────────────────────
  {
    target: '#tour-deck-1-card-4',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: '⚡ What\'s the catch?',
    content: 'Click the Conflict button — every good story needs a complication.',
  },
  // ── Step 19: Select an Conflict option ──────────────────────────────────
  {
    target: '#tour-card-properties',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '✏️ Pick an Option',
    content:
      'Look at the right panel and choose the option that fits your story.\n\n' +
      'There is no set order in which you need to perform actions, you can chose the option first or move the card first.\n\n' +
      'At any time you can select a card on the canvas by left-clicking it, and change the option to make it fit your story better. ',
  },
  // ── Step 20: Place the Conflict card ─────────────────────────────────────
  {
    target: '#tour-canvas-grid',
    placement: 'top-start',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '📐 Place the Conflict',
    content:
      'Now drag the card to the right of the Anchor card.\n\n' +
      'If you need more space, you can move the entire canvas using the middle mouse button or use the scroll wheel to zoom out.',
  },

  // ── Step 21: Draw an Aspect card ─────────────────────────────────────────
  {
    target: '#tour-deck-1-card-3',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: '🎭 Add a Detail',
    content: 'Click the Aspect button to add a vivid quality to your story.',
  },
  // ── Step 22: Select an Aspect option ──────────────────────────────────
  {
    target: '#tour-card-properties',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '✏️ Pick an Option',
    content:
      'Look at the right panel and choose the option that fits your story. This can relate to the Actor or Anchor card.',
  },
  // ── Step 23: Place Aspect card ───────────────────────────────────────────
  {
    target: '#tour-canvas-grid',
    placement: 'top-start',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '📐 Place the Detail',
    content:
      'Move the card above or below the card to make it say something about that card.',
  },

  // ── Step 24: Continue with user interface ───────────────
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: '📖 Continue the journey',
    content:
      'Your first story is done! Later you can draw more cards to expand on your story or start a new story. ' +
      'Let\'s continue with the rest of the user interface.',
  },

  // ── Step 25: Selecting multiple cards ───────────────────────────────────────────
  {
    target: '#tour-canvas-grid',
    placement: 'top-start',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '📐 Move things around',
    content:
      'You can select multiple cards by holding down the left mouse button and dragging across the canvas, ' +
      'this way you can move cards around easier or delete a group of cards at once.\n\n' +
      'When moving cards you can only release them in an empty spot, otherwise they will snap back to their original position.\n\n' +
      'Be aware: You can not undo a deletion and the chance that you draw the same cards again is small.\n\n' +
      'Now click anywhere on the canvas to deselect all cards.',
  },
  // ── Step 26: Mini map ───────────────────────────────────────────────
  {
    target: '#tour-minimap',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '🗺️ Don\'t get lost',
    content:
      'This is the minimap. It shows you the entire canvas and allows you to quickly navigate to different parts of your story.\n\n' +
      'The yellow square shows the current view of the canvas, you can see it grow and shrink when you zoom in and out of the canvas.\n\n' +
      'Click anywhere in the minimap to jump to that area. You can also pan around the canvas using the middle mouse button.',
  },
  // ── Step 27: Rename Story ──────────────────────────────────────────────────────
  {
    target: '#tour-rename-story',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    title: '✍️ Give your story a name',
    content: 'Give your story a name so you can find it back later.',
  },
  // ── Step 28: Select language ──────────────────────────────────────────────────────
  {
    target: '#tour-language-select',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    title: '🗣️ Change language',
    content: 'You can change the language of the decks and the cards by picking another language from the dropdown.',
  },

  // ── Step 29: Select decks ──────────────────────────────────
  {
    target: '#tour-deck-selector',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '📚 Select some more decks',
    content:
      'The main decks have several expansions, each containing cards in a specific genre. Select some expansions to expand your deck.\n\n' +
      'You can select multiple expansions at the same time, like Dystopian and Militaity or Fantasy and Fairy tale.',
  },
  // ── Step 30: Shuffle into main deck ──────────────────────────────────
  {
    target: '#tour-shuffle-extension',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '📚 Shuffle Your Decks',
    content:
      'The expansion decks can be shuffled into the main deck or kept separate, this means you can choose to mix your decks together into one big deck ' +
      'or keep them separate giving you more control over the cards you draw. Try toggling "Shuffle Extension Decks into Main deck" ' +
      'to see the difference in the left panel.',
  },
  // ── Step 31: Custom Background Images ──────────────────────────────────
  {
    target: '#tour-custom-background-images',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '📚 Custom Background Images',
    content:
      'Custom Background Images sometimes make it hard to read the card text or interfere with your imagination. ' +
      'Try toggling "Custom Background Images" to see the difference on the cards.',
  },
  // ── Step 32: Canvas Background ──────────────────────────────────
  {
    target: '#tour-canvas-bg',
    placement: 'left',
    disableBeacon: true,
    spotlightClicks: true,
    disableScrolling: true,
    title: '🎨 Canvas Background',
    content:
      'Canvas Backgrounds can make playing with the cards more immersive and fun. ' +
      'Try selecting a background and see the difference on the canvas.',
  },

  // ── Step 33: Download PNG ──────────────────────────────────────────────────────
  {
    target: '#tour-export-png',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: '📸 Export as Image',
    content: 'Click the Download button to save an image of your canvas to share or print.',
  },

  // ── Step 34: Export AI Prompt ──────────────────────────────────────────────────────
  {
    target: '#tour-export-ai-story',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: '✨ Export AI Story',
    //  content: 'Click the "Export AI Story" button to generate an AI prompt for your story.',
    content: "This button currently does not work properly. It will be fixed in a future update.",
  },

  // ── Step 25: Back to Dashboard ───────────────────────────────────────────
  {
    target: '#tour-back-dashboard',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: '🏠 Back to Your World!',
    content:
      'Click the back arrow to return to the Dashboard. ' +
      'Your canvas is saved automatically.',
  },

  // ── Step 26: Outro (back on Dashboard) ──────────────────────────────────
  {
    target: '#tour-tours-btn',
    placement: 'bottom',
    disableBeacon: true,
    title: '🎊 You\'re a Story Weaver!',
    content:
      'Well done — you just built your first story seed! ' +
      'Click the 🗺️ Guided Tours button any time to explore the decks and features in more depth.',
  },
];

export default introSteps;
